/**
 * QR Code  component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import { QRCodeSVG } from "qrcode.react";

import { Type, defineElementaryComponent, BP } from "@adapptio/lib-blueprint";

import { termsEditor } from "../../terms";
import {
	BackgroundColorSchema,
	ClassList,
	ForegroundColorSchema,
	getStringEnumValue,
	getValuesFromStringEnum,
	LinkSchema,
	propGroups,
	RoutingContext,
	StyleSheet,
	TViewComponentDefinition,
	TComponentReact,
	useStyleSheetRegistry
} from "@adapptio/lib-components";

import {
	QR_CODE_ERROR_CORRECTION_LEVEL,
	QR_CODE_ERROR_CORRECTION_LEVEL_default
} from "../../Enums/QR_CODE_ERROR_CORRECTION_LEVEL";
import { isBoolean, isNonEmptyString } from "@adapptio/lib-shared";

interface ComponentQrCode_State {}

const ComponentQrCode_Props = {
	valueData: BP.Prop(
		BP.OneOf({
			...termsEditor.schemas.qrCode.valueData,
			typeValueOpts: {
				...termsEditor.schemas.qrCode.valueData
			},
			defaultType: "TEXT",
			types: {
				TEXT: {
					...termsEditor.schemas.qrCode.valueDataValues.text,
					value: BP.String({
						default: ""
					})
				},
				LINK: {
					...termsEditor.schemas.qrCode.valueDataValues.link,
					value: LinkSchema({
						alias: "link_qrCode",
						props: {
							enabled: BP.Prop(BP.Void({}))
						}
					})
				}
			},
			constraints: {
				required: true
			},
			editorOptions: {
				layoutType: "noHeader"
			}
		}),
		0,
		propGroups.common
	),

	errorCorrectionLevel: BP.Prop(
		BP.Enum.String({
			...termsEditor.schemas.qrCode.errorCorrectionLevel,
			options: getValuesFromStringEnum(QR_CODE_ERROR_CORRECTION_LEVEL),
			default: QR_CODE_ERROR_CORRECTION_LEVEL_default,
			fallbackValue: QR_CODE_ERROR_CORRECTION_LEVEL_default,
			constraints: {
				required: true
			},
			editorOptions: {
				controlType: "buttons"
			}
		}),
		10,
		propGroups.common
	),

	backgroundColor: BP.Prop(
		BackgroundColorSchema({
			alias: "backgroundColor_qrCode",
			default: "WHITE"
		}),
		10,
		propGroups.style
	),

	foregroundColor: BP.Prop(
		ForegroundColorSchema({
			alias: "foregroundColor_qrCode",
			default: "BLACK"
		}),
		20,
		propGroups.style
	)
};

const ComponentQrCode_Events = {};

const ComponentQrCode_Definition = defineElementaryComponent<
	typeof ComponentQrCode_Props,
	ComponentQrCode_State,
	typeof ComponentQrCode_Events
>({
	...termsEditor.components.qrCode.component,

	name: "qrcode",

	category: "content",

	icon: "mdi/qrcode",

	events: ComponentQrCode_Events,

	docUrl: "...",

	order: 220,

	props: ComponentQrCode_Props,

	resolve: (spec, state) => {
		return state || {};
	},

	getScopeData: (spec, state) => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentQrCode_React: TComponentReact<typeof ComponentQrCode_Definition> = ({
	props,
	componentInstance,
	reactComponentClassList
}) => {
	const { valueData, errorCorrectionLevel, backgroundColor, foregroundColor } = props;

	const routingContext = React.useContext(RoutingContext);

	let value = "";

	switch (valueData.type) {
		case "TEXT":
			value = valueData.value[valueData.type];
			break;

		case "LINK": {
			const link = valueData.value[valueData.type];

			const resolvedLink = routingContext.resolveLink(link.locationData, {
				exact: isBoolean(link.exact) ? link.exact : false
			});

			value = resolvedLink.url;
		}
	}

	const { classList, idClassName } = ClassList.getElementClassListAndIdClassName(
		"cmp-qr-code",
		componentInstance.safePath,
		{ componentClassList: reactComponentClassList }
	);

	const styleSheetRegistry = useStyleSheetRegistry();

	const styleSheet = React.useMemo(() => {
		const result = new StyleSheet();

		// Background color

		if (backgroundColor) {
			result.addColorProperties({
				selector: `.${idClassName}`,
				name: "element-background-color",
				value: backgroundColor
			});
		}

		// Foreground color

		if (foregroundColor) {
			result.addColorProperties({
				selector: `.${idClassName}`,
				name: "element-foreground-color",
				value: foregroundColor
			});
		}

		return result;
	}, [ idClassName, backgroundColor, foregroundColor ]);

	styleSheetRegistry.add(idClassName, styleSheet);

	if (!isNonEmptyString(value)) {
		return null;
	}

	const errorCorrectionLevelValue = getStringEnumValue(
		QR_CODE_ERROR_CORRECTION_LEVEL,
		errorCorrectionLevel,
		QR_CODE_ERROR_CORRECTION_LEVEL_default
	);

	return (
		<div className={classList.toClassName()}>
			<QRCodeSVG
				value={value}
				level={errorCorrectionLevelValue}
				size={256}
				bgColor={"var(--element-background-color)"}
				fgColor={"var(--element-foreground-color)"}
			/>
		</div>
	);
};

export const ComponentQrCode: TViewComponentDefinition<typeof ComponentQrCode_Definition> = {
	...ComponentQrCode_Definition,
	reactComponent: ComponentQrCode_React
};
